import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { reduxForm, SubmissionError } from 'redux-form'
// hooks
import { useSnackbar } from 'notistack'
// components
import { InputAdornment } from '@mui/material'
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { Button, ErrorMessage, Link } from 'components/UI'
// constants
import { Forms } from 'constants/forms'
// graphql
import { AUTH_RESET_PASSWORD_MUTATION } from 'graphql/auth/mutations'
// views
import { AuthForm, AuthHeader, ButtonWrapper } from 'themes/views/auth'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// utils
import { requiredEmpty } from 'utils/validators'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type FormData = {
  email: string
  domain: string
}

const ResetPassword: React.FC<InjectedFormProps<FormData>> = ({ valid, error, change, handleSubmit }) => {
  const router = useRouter()

  const [resetPasswordMutation] = useMutation(AUTH_RESET_PASSWORD_MUTATION)

  const [isEmailSent, setIsEmailSent] = React.useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const onFormSubmit = async (data: FormData) => {
    try {
      await resetPasswordMutation({
        variables: {
          email: data.email,
          domain: `${data.domain}.skefto.com`
        }
      })

      setIsEmailSent(true)

      enqueueSnackbar('Please check your inbox for instructions on how to change your password!', {
        variant: 'info',
        autoHideDuration: null
      })
    } catch (errors) {
      console.log('[ResetPassword.onFormSubmit]: ', parseGraphQLErrorMessages(errors))
      throw new SubmissionError({ _error: parseGraphQLErrorMessages(errors) })
    }
  }

  React.useEffect(() => {
    router.query.email && change('email', router.query.email)
    router.query.domain && change('domain', router.query.domain)
  }, [router.query])

  return (
    <>
      <AuthHeader>CHANGE PASSWORD</AuthHeader>
      {isEmailSent ? (
        <Link href='/login'>Login</Link>
      ) : (
        <AuthForm onSubmit={handleSubmit(onFormSubmit)}>
          <ReduxInput
            type='text'
            name='domain'
            label='Domain'
            validate={requiredEmpty}
            endAdornment={<InputAdornment position='end'>.skefto.com</InputAdornment>}
          />
          <div>
            <ReduxInput type='text' name='email' label='Email or Username' validate={[requiredEmpty]} />
            <Link href='/login'>Back to login</Link>
          </div>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <ButtonWrapper>
            <Button variant='contained' fullWidth type='submit' disabled={!valid}>
              SUBMIT
            </Button>
          </ButtonWrapper>
        </AuthForm>
      )}
    </>
  )
}

export default reduxForm<FormData>({ form: Forms.RESET_PASSWORD_FORM })(ResetPassword)
